/**
 * Resolve the dynamic "discounted from" override (in minor units, e.g. cents)
 * from the URL query for a given price.
 *
 * A per-price `${reference}_discounted_from` param takes precedence over the
 * legacy, product-level `discounted_from` (kept for back-compat). Returns `NaN`
 * when no override is present, so the caller falls back to the price's stored
 * `discountedFromPrice`.
 *
 * Callers MUST gate on the merchant's `allowDynamicDiscountedFromPrice` setting
 * before applying the result, and treat negatives as "no override" (`>= 0`) —
 * matching the legacy single-price behaviour.
 *
 * `allowLegacyFallback` MUST be false on a multi-price product: the unprefixed
 * param is a single-price back-compat shim and would otherwise hit every option
 * at once. See queryPriceField.
 *
 * @example ?price_monthly_discounted_from=1900  → 1900 for the "price_monthly" option
 * @example ?discounted_from=1900                → 1900 fallback, single-price only
 *
 * @param {string|undefined} reference  the price's `reference`
 * @param {Object|undefined} query      the URL query object
 * @param {{allowLegacyFallback?: boolean}} [options]
 * @returns {number} the override amount in minor units, or NaN when none applies
 */
export function queryDiscountedFrom(reference, query, { allowLegacyFallback = true } = {}) {
  if (reference != null && reference !== '') {
    const perPrice = Number(query?.[`${reference}_discounted_from`]);
    if (perPrice >= 0) return perPrice;
  }
  return allowLegacyFallback ? Number(query?.discounted_from) : NaN;
}

/**
 * Resolve a per-price URL override for `field`, keyed by the price's `reference`
 * (`${reference}_${field}`, e.g. `prc_ab12_label`). Optionally falls back to the
 * legacy, unprefixed `${field}` param, then `undefined`.
 *
 * Returns the raw string value — callers coerce (e.g. `Number()` for `price`) and
 * MUST gate on the matching merchant `allowDynamic*` flag before applying. A
 * reference is matched exactly (never parsed from arbitrary keys), so references
 * containing underscores are unambiguous.
 *
 * `allowLegacyFallback` MUST be false on a multi-price product. The unprefixed
 * params (`price`, `label`, `description`, `discounted_from`) predate prices[]
 * and carry no reference, so on a multi-price product they would apply to every
 * option indiscriminately — a `?price=` link would silently reprice every tier.
 * Multi-price checkouts are addressed only by `${reference}_${field}`.
 *
 * @example queryPriceField('prc_ab12', 'label', { 'prc_ab12_label': 'VIP' }) → 'VIP'
 * @example queryPriceField('prc_ab12', 'price', { price: '4900' })          → '4900' (single-price only)
 *
 * @param {string|undefined} reference  the price's `reference`
 * @param {string} field                the field name (price | label | description | pwyw | no_of_payments | …)
 * @param {Object|undefined} query      the URL query object
 * @param {{allowLegacyFallback?: boolean}} [options]
 * @returns {string|undefined} the override value, or undefined when none applies
 */
export function queryPriceField(reference, field, query, { allowLegacyFallback = true } = {}) {
  if (reference != null && reference !== '') {
    const perPrice = query?.[`${reference}_${field}`];
    if (perPrice != null && perPrice !== '') return perPrice;
  }
  if (!allowLegacyFallback) return undefined;
  const legacy = query?.[field];
  return legacy != null && legacy !== '' ? legacy : undefined;
}
